/**
 * Searchable dropdown for selecting redaction reasons.
 * Lists reasons A–Z by title, one title per row; the citation and full legal
 * text show on hover. Search matches the title, description, citation and
 * legal text.
 *
 * When the search names no existing reason, the picker offers to create one
 * ("Create reason "…""), which opens the reason form prefilled with the search and
 * selects the new reason once saved (FB-1013).
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, ChevronDown, Check, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import type { RedactionReason } from '../../types';
import { cn } from '../ui/utils';
import { CreateReasonDialog } from '../management/CreateReasonDialog';
import { filterReasons, reasonDetails, reasonTitleToCreate, sortReasonsByTitle } from './reasonOptions';

interface SearchableReasonSelectProps {
  reasons: RedactionReason[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Offer "Create reason" for a search that matches no title (default on). */
  allowCreate?: boolean;
}

export function SearchableReasonSelect({
  reasons,
  value,
  onChange,
  placeholder = 'Select reason...',
  allowCreate = true,
}: SearchableReasonSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // The title the create form opened with; null while it's closed.
  const [creatingTitle, setCreatingTitle] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const sortedReasons = useMemo(() => sortReasonsByTitle(reasons), [reasons]);
  const filteredReasons = useMemo(() => filterReasons(sortedReasons, search), [sortedReasons, search]);
  const createTitle = allowCreate ? reasonTitleToCreate(reasons, search) : null;

  // Find selected reason
  const selectedReason = reasons.find((r) => r.title === value);

  // Focus search input when popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
    // Keep the search while the create form is open: Cancel comes back to it.
    if (!open && creatingTitle === null) {
      setSearch('');
    }
  }, [open, creatingTitle]);

  const handleSelect = (reason: RedactionReason) => {
    onChange(reason.title);
    setOpen(false);
  };

  // The popover closes so the form (a modal) is the only layer on top.
  const handleCreate = () => {
    if (!createTitle) return;
    setCreatingTitle(createTitle);
    setOpen(false);
  };

  const handleCreated = (reason: RedactionReason) => {
    setCreatingTitle(null);
    onChange(reason.title);
  };

  const handleCancelCreate = () => {
    setCreatingTitle(null);
    setOpen(true);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex items-center justify-between h-7 w-full px-2 text-xs border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors text-left',
            !value && 'text-gray-400'
          )}
        >
          <span className="truncate" title={selectedReason?.title}>
            {selectedReason ? selectedReason.title : placeholder}
          </span>
          <ChevronDown className="h-3 w-3 ml-1 flex-shrink-0 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        // Above the popups that hold a picker (the manual-redaction picker sits
        // at z-index 9999 and used to cover this list), like the create form.
        className="z-[10000] w-[280px] p-0 bg-white border border-gray-200 shadow-lg rounded-lg"
        align="start"
        sideOffset={4}
        // Closing for the create form: focus goes into the form, not back to the button.
        onCloseAutoFocus={(e) => {
          if (creatingTitle !== null) e.preventDefault();
        }}
      >
        {/* Search input */}
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reasons..."
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Reasons list */}
        <ReasonOptionList reasons={filteredReasons} value={value} onSelect={handleSelect} />
        {createTitle && (
          <CreateReasonOption title={createTitle} onCreate={handleCreate} separated={filteredReasons.length > 0} />
        )}

        {/* Clear selection option */}
        {value && (
          <div className="border-t border-gray-100 p-1">
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 rounded transition-colors"
            >
              Clear selection
            </button>
          </div>
        )}
      </PopoverContent>
      {/* A sibling of the popover's content, so it outlives the popover closing. */}
      {allowCreate && (
        <CreateReasonDialog
          open={creatingTitle !== null}
          initialTitle={creatingTitle ?? ''}
          reasons={reasons}
          onCreated={handleCreated}
          onCancel={handleCancelCreate}
        />
      )}
    </Popover>
  );
}

interface CreateReasonOptionProps {
  /** The search, as the new reason's title. */
  title: string;
  onCreate: () => void;
  /** Rule it off from reason rows above it. */
  separated: boolean;
}

/** "Create reason "…"": below the matches, or under "No reasons found". */
export function CreateReasonOption({ title, onCreate, separated }: CreateReasonOptionProps) {
  return (
    <div className={cn('p-1', separated && 'border-t border-gray-100')}>
      <button
        type="button"
        onClick={onCreate}
        data-testid="reason-create-option"
        className="w-full text-left px-2 py-1.5 hover:bg-gray-50 rounded transition-colors flex items-start gap-2 text-sm text-gray-900"
      >
        <Plus className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-gray-500" aria-hidden />
        <span className="flex-1 min-w-0 break-words">
          Create reason <span className="font-medium">“{title}”</span>
        </span>
      </button>
    </div>
  );
}

interface ReasonOptionListProps {
  /** Already sorted and filtered. */
  reasons: RedactionReason[];
  value: string;
  onSelect: (reason: RedactionReason) => void;
}

/** The rows of the picker: the title only, with the legal reference on hover. */
export function ReasonOptionList({ reasons, value, onSelect }: ReasonOptionListProps) {
  if (reasons.length === 0) {
    return <div className="px-3 py-4 text-center text-sm text-gray-500">No reasons found</div>;
  }
  return (
    <div role="listbox" aria-label="Redaction reasons" className="max-h-[240px] overflow-y-auto py-1">
      {reasons.map((reason) => {
        const isSelected = reason.title === value;
        const details = reasonDetails(reason);
        const row = (
          <button
            type="button"
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(reason)}
            className={cn(
              'w-full text-left px-3 py-1.5 hover:bg-gray-50 transition-colors flex items-start gap-2',
              isSelected && 'bg-gray-50'
            )}
          >
            {isSelected ? (
              <Check className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-gray-900" />
            ) : (
              <span className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            )}
            <span className="flex-1 min-w-0 break-words text-sm text-gray-900">{reason.title}</span>
          </button>
        );
        if (!details) return <div key={reason.id}>{row}</div>;
        return (
          <Tooltip key={reason.id}>
            <TooltipTrigger asChild>{row}</TooltipTrigger>
            <TooltipContent
              side="right"
              align="start"
              className="z-[10000] max-w-sm max-h-[60vh] font-normal leading-relaxed text-left"
            >
              {details.citation && <p className="font-semibold">{details.citation}</p>}
              {details.paragraphs.map((text, i) => (
                <p key={i} className={cn('whitespace-pre-line', (i > 0 || details.citation) && 'mt-1')}>
                  {text}
                </p>
              ))}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}
